import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { useCategoryEditDialog } from "./useCategoryEditDialog";
import { TemplateCategoryMasterDto } from "@/dto/TemplateCategoryDto";

interface CategoryEditDialogProps {
  open: boolean;
  onClose: () => void;
  selectedCategory: TemplateCategoryMasterDto | null;
  isLoading: boolean;
  onSubmit: (data: any) => Promise<boolean>;
}

export const CategoryEditDialog = ({
  open,
  onClose,
  selectedCategory,
  isLoading,
  onSubmit,
}: CategoryEditDialogProps) => {
  const { editForm, handleSubmit } = useCategoryEditDialog({
    selectedCategory,
    onSubmit,
  });

  if (!selectedCategory) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
    >
      <DialogTitle>
        {selectedCategory?.categoryLevel === 1 ? "페이지" : "섹션"} 수정
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box
            sx={{
              py: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography>로딩 중...</Typography>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* 카테고리 이름 */}
              <Controller
                name="categoryName"
                control={editForm.control}
                rules={{ required: "카테고리 이름을 입력하세요" }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <FormLabel required sx={{ fontWeight: 500, mb: 1 }}>
                      {selectedCategory?.categoryLevel === 1
                        ? "페이지 이름"
                        : "섹션 이름"}
                    </FormLabel>
                    <TextField
                      {...field}
                      size="small"
                      placeholder="예: 메인 페이지, 헤더 섹션"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  </FormControl>
                )}
              />

              {/* 카테고리 설명 */}
              <Controller
                name="categoryDesc"
                control={editForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                      {selectedCategory?.categoryLevel === 1
                        ? "페이지 설명"
                        : "섹션 설명"}
                    </FormLabel>
                    <TextField
                      {...field}
                      size="small"
                      multiline
                      rows={2}
                      placeholder="용도와 특징을 간략히 설명해주세요"
                    />
                  </FormControl>
                )}
              />

              {/* 노출 순서 */}
              <Controller
                name="dispOrder"
                control={editForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                      노출 순서
                    </FormLabel>
                    <TextField
                      {...field}
                      size="small"
                      type="number"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                    <FormHelperText>
                      낮은 숫자가 먼저 표시됩니다 (0부터 시작)
                    </FormHelperText>
                  </FormControl>
                )}
              />

              {/* 앵커 ID */}
              <Controller
                name="anchorId"
                control={editForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                      앵커 ID
                    </FormLabel>
                    <TextField
                      {...field}
                      size="small"
                      placeholder="예: main-section, about-us"
                    />
                    <FormHelperText>
                      페이지 내 이동에 사용되는 고유 ID (영문, 숫자, 하이픈만
                      사용)
                    </FormHelperText>
                  </FormControl>
                )}
              />

              {/* 데모 페이지 URL */}
              <Controller
                name="demoPageUrl"
                control={editForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                      데모 페이지 URL
                    </FormLabel>
                    <TextField
                      {...field}
                      size="small"
                      placeholder="https://example.com/demo"
                    />
                  </FormControl>
                )}
              />

              {/* 위치 설명 */}
              <Controller
                name="locationDesc"
                control={editForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                      위치 설명
                    </FormLabel>
                    <TextField
                      {...field}
                      size="small"
                      placeholder="예: 헤더 아래, 푸터 위"
                    />
                  </FormControl>
                )}
              />

              {/* 스크린샷 URL */}
              <Controller
                name="screenshotUrl"
                control={editForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <FormLabel sx={{ fontWeight: 500, mb: 1 }}>
                      스크린샷 URL
                    </FormLabel>
                    <TextField
                      {...field}
                      size="small"
                      placeholder="https://example.com/screenshot.jpg"
                    />
                  </FormControl>
                )}
              />
            </Stack>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button
          variant="contained"
          onClick={editForm.handleSubmit((data) => onSubmit(data))}
          disabled={isLoading}
        >
          {isLoading ? "처리 중..." : "수정"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
